import { formatDateNumber } from '@seen/core/dates'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { ValueSetAutocomplete } from '#src/components/ValueSetAutocomplete/ValueSetAutocomplete.tsx'
import { useForm } from '#src/hooks/useForm.ts'

// The allergen picker unions two terminologies so plain foods (SNOMED) are not
// crowded out of the result window by RxNorm drug/substance products. Each
// binding is expanded independently and merged (see
// valueSetAutocompleteLoadOptions), so neither system can starve the other
// before client-side ranking.
const ALLERGEN_BINDINGS = [
  // SNOMED CT food / substance / environmental allergens.
  'http://hl7.org/fhir/ValueSet/allergyintolerance-code',
  // Seen's combined set; contributes the RxNorm medication/substance allergens.
  'https://www.seenhealth.org/fhir/ValueSet/allergy-intolerances',
]
const ALLERGEN_COUNT_PER_BINDING = 30

export const allergyFormSchema = z.object({
  allergy: z.object({
    code: z.string(),
    display: z.string(),
    system: z.string(),
  }),
  reaction: z.string().nullable(),
  reactionCode: z.string().nullable(),
  severity: z.enum(['mild', 'moderate', 'severe']).nullable(),
  status: z.enum(['active', 'inactive']),
  onsetDate: z.string().nullable(),
  type: z.enum(['allergy', 'intolerance']),
})

export type AllergyFormValues = z.infer<typeof allergyFormSchema>

export function AllergyForm({
  onSubmit,
  loading,
}: {
  onSubmit: (values: AllergyFormValues) => void
  loading: boolean
}): JSX.Element {
  const form = useForm({
    schema: allergyFormSchema,
    defaultValues: {
      allergy: {
        code: '',
        display: '',
        system: '',
      },
      reaction: null,
      reactionCode: null,
      severity: null,
      status: 'active',
      onsetDate: null,
      type: 'allergy',
    },
  })

  return (
    <Form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
    >
      <FormField
        control={form.control}
        name="allergy"
        render={({ field: { value, onChange, ...fieldRest } }) => {
          const { code, display, system } = value

          return (
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">
                Allergy <span className="text-orange-500">*</span>
              </span>
              <ValueSetAutocomplete
                binding={ALLERGEN_BINDINGS}
                countPerBinding={ALLERGEN_COUNT_PER_BINDING}
                values={code ? [{ code, display, system }] : []}
                onSelect={(value) => {
                  onChange(value ?? { code: '', display: '', system: '' })
                }}
                {...fieldRest}
              >
                <Button variant="outline" className="w-full justify-between">
                  {code ? `${code} - ${display}` : 'Search…'}
                </Button>
              </ValueSetAutocomplete>
            </label>
          )
        }}
      />
      <FormField
        control={form.control}
        name="reaction"
        render={({ field: { value, onChange, ...fieldRest } }) => (
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Reaction</span>
            <RichTextEditor
              simpleText
              value={value ?? ''}
              onChange={onChange}
              variant="underline"
              {...fieldRest}
            />
          </label>
        )}
      />
      <FormField
        control={form.control}
        name="reactionCode"
        render={({ field: { value, onChange, ...fieldRest } }) => (
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Reaction Code</span>
            <RichTextEditor
              simpleText
              value={value ?? ''}
              onChange={onChange}
              variant="underline"
              {...fieldRest}
            />
          </label>
        )}
      />
      <div className="flex gap-4">
        <FormField
          control={form.control}
          name="type"
          render={({ field: { value, onChange, ...fieldRest } }) => (
            <label className="flex w-fit flex-col gap-1">
              <span className="text-sm font-medium">Type</span>
              <Combobox
                {...fieldRest}
                options={[
                  {
                    label: 'Allergy',
                    value: 'allergy',
                    keywords: ['allergy'],
                  },
                  {
                    label: 'Intolerance',
                    value: 'intolerance',
                    keywords: ['intolerance'],
                  },
                ]}
                onSelect={onChange}
              >
                <Button variant="outline" className="w-fit">
                  {value === 'allergy' ? 'Allergy' : 'Intolerance'}
                </Button>
              </Combobox>
            </label>
          )}
        />
        <FormField
          control={form.control}
          name="severity"
          render={({ field: { value, onChange, ...fieldRest } }) => (
            <label className="flex w-fit flex-col gap-1">
              <span className="text-sm font-medium">Severity</span>
              <Combobox
                {...fieldRest}
                options={[
                  {
                    label: 'Mild',
                    value: 'mild',
                    keywords: ['mild'],
                  },
                  {
                    label: 'Moderate',
                    value: 'moderate',
                    keywords: ['moderate'],
                  },
                  {
                    label: 'Severe',
                    value: 'severe',
                    keywords: ['severe'],
                  },
                ]}
                onSelect={onChange}
              >
                <Button variant="outline" className="w-fit">
                  {value ?? '--'}
                </Button>
              </Combobox>
            </label>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field: { value, onChange, ...fieldRest } }) => (
            <label className="flex w-fit flex-col gap-1">
              <span className="text-sm font-medium">Status</span>
              <Combobox
                {...fieldRest}
                options={[
                  {
                    label: 'Active',
                    value: 'active',
                    keywords: ['active'],
                  },
                  {
                    label: 'Inactive',
                    value: 'inactive',
                    keywords: ['inactive'],
                  },
                ]}
                onSelect={onChange}
              >
                <Button variant="outline" className="w-fit">
                  {value ?? 'Select Status'}
                </Button>
              </Combobox>
            </label>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="onsetDate"
        render={({ field: { value, onChange, ...fieldRest } }) => (
          <label className="flex w-fit flex-col gap-1">
            <span className="text-sm font-medium">Onset Date</span>
            <DatePicker
              value={value ?? ''}
              onChange={(value) => onChange(value ?? null)}
              placeholder="e.g. YYYY"
              dateFormatter={(date) => formatDateNumber(date)}
              clearText=""
              {...fieldRest}
            />
          </label>
        )}
      />
      <div className="flex justify-end">
        <Button type="submit" loading={loading}>
          Add Allergy
        </Button>
      </div>
    </Form>
  )
}
